import Config from 'config/settings';
import { geometry, Vector2 } from 'app/contracts/Geometry';
import { particles } from 'app/contracts/Particles';
import { ParticleLayoutType } from 'app/enum/Particles';
import { HomeField } from 'app/views/home/webgl/particles/fields/HomeField';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import BaseDepartmentScene from 'app/views/home/webgl/particles/departments/BaseDepartmentScene';
import { device } from 'utils/device';

export default class IconDepartment extends BaseDepartmentScene {
	private _spawnCounter: number = 0;

	private _iconParticles: Array<HomeParticle>;
	private _iconPixelData: Array<Vector2>;
	private _iconParticleContainer: PIXI.Sprite;

	private _iconScaleUpFrameCounter = 0;
	private _iconScaleUpFrames = 3;
	private _iconScaleUpActive = true;
	private _iconGridWidth;
	private _iconGridHeight;
	private _iconAddEachPass = 4;
	private _iconReduceEachPass = 0.99;
	private _iconScaleUpPassCount = 0;
	private _iconScaleUpTotalPasses = 350;
	private _targetZone = null;
	private _fromIntro: boolean = true;
	private _mobileMode: boolean = false;

	constructor(
		private _homeField: HomeField,
		private _sceneProps: particles.SceneProps
	) {
		super();
		this._iconParticleContainer = this._homeField.getSubContainer('icon');

		this._iconParticles = HomeFieldConfig.getParticlePool(this._homeField.particles as Array<HomeParticle>, 'icon');

		this._mobileMode = device.innerWidth <= Config.mobileBreakpoint;
		if (this._mobileMode) {
			this._iconParticles = this._iconParticles.slice(0, HomeFieldConfig.iconParticlesMobile);
		}

		this._iconParticles.forEach((element) => {
			element.activate();
		});

		this._setIntialCentered();
	}

	public setIconPixelData(iconPixelData: Array<Vector2>) {
		this._iconPixelData = iconPixelData;
		HomeFieldConfig.iconParticles = iconPixelData.length;
	}

	public tick() {
		this._positionParticleContainer();
		this._iconScaleUpTick();

		if (!this._sceneProps.flags.transitioning) {
			this._spawnCounter--;
			if (this._spawnCounter <= 0) {
				this._spawnCounter = Math.round(180 + Math.random() * 30);
				this._spawnCounter = 10000000000000;
				this._iconParticles.forEach((p) => {
					let power = 3 + Math.random() * 10;
					p.icon.switchHomeEffect(power);
				});
			}
		}
	}

	public startAsActiveScene() {
		this._iconParticleContainer.visible = true;
		this._toIconParticles();
	}

	public stopAsActiveScene() {
		this._iconParticleContainer.visible = false;

		this._iconParticles.forEach((particle: HomeParticle) => {
			particle.deactivateAllBehaviours();
			particle.active = false;
		});
	}

	public startSceneDrag() {
		this._iconParticles.forEach((particle, index) => {
			particle.icon.startSceneDrag();
		});
	}

	public endSceneDrag() {
		this._iconParticles.forEach((particle, index) => {
			particle.icon.endSceneDrag();
		});
	}

	//PRIVATE
	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------

	private _positionParticleContainer() {
		let x =
			this._sceneProps.progress.containerX +
			this._sceneProps.zonePositions.homeParticles.cx * this._sceneProps.sizes.container.w;
		this._sceneProps.mouse.particleContainersX.home = x;
		this._iconParticleContainer.x = x;
	}

	private _setIntialCentered(): void {
		let isMobile = device.innerWidth < 1200;
		let x = isMobile ? 0.01 : -0.042;

		this._iconParticles.forEach((p) => {
			p.position.set(
				{
					x: x,
					y: 0.5
				},
				true
			);

			p.animatedProps.targetAlpha = 1;
			p.animatedProps.targetScale = 0;
		});
	}

	private _toIconParticles(): void {
		let startScale = 1.3;

		if (device.innerWidth < 600) {
			startScale *= 0.6;
		}

		let zone = this._sceneProps.zones.home;
		let nw = 229 * startScale;
		let nh = 352 * startScale;


		let size: geometry.Size = {
			w: (nw / this._sceneProps.sizes.container.w) * zone.scale,
			h: (nh / device.innerHeight) * zone.scale
		};

		let position: geometry.Vector2 = {
			x: 0 - size.w / 2 + 0.0005,
			y: zone.cy - size.h / 2 - 0.0
		};

		if (this._fromIntro) {
			this._setIntialCentered();
		}

		this._homeField.reposition(
			ParticleLayoutType.IMAGE_PIXEL_DATA,
			position,
			size,
			{ pixelData: this._iconPixelData },
			this._iconParticles.slice(0, this._iconPixelData.length),
			500,
			false,
			this._afterParticlePostion.bind(this),
			this._mobileMode
		);

		this._iconGridWidth = nw;
		this._iconGridHeight = nh;
		this._targetZone = zone;

		this._iconScaleUpActive = false;

		if (this._fromIntro) {
			this._startIconScaleUp();
		} else {
			setTimeout(() => {
				this._startIconScaleUp();
			}, 400);
		}

		this._fromIntro = false;
	}

	private _afterParticlePostion(particle: HomeParticle) {
		particle.activate();
		particle.icon.initIcon();

		if (Math.random() < 0.07) {
			particle.colour.set('#069c8a');
		}
	}

	private _startIconScaleUp() {
		this._iconScaleUpActive = true;
		this._iconScaleUpFrameCounter = this._iconScaleUpPassCount = 0;
		this._iconAddEachPass = 6;
		this._iconScaleUpTotalPasses = 450;
		this._iconReduceEachPass = 0.98;
		this._iconScaleUpFrames = 1;
	}

	private _iconScaleUpTick() {
		if (!this._iconScaleUpActive) {
			return;
		}

		this._iconScaleUpFrameCounter += (window as any).deltaTime;
		if (this._iconScaleUpFrameCounter >= this._iconScaleUpFrames) {
			this._iconScaleUpFrameCounter = 0;
			this._increaseIconSizePass();
		}
	}

	private _increaseIconSizePass() {
		this._iconGridWidth += this._iconAddEachPass;
		this._iconGridHeight += this._iconAddEachPass;
		this._iconAddEachPass *= this._iconReduceEachPass;

		let size: geometry.Size = {
			w: (this._iconGridWidth / this._sceneProps.sizes.container.w) * this._targetZone.scale,
			h: (this._iconGridHeight / device.innerHeight) * this._targetZone.scale
		};

		let position: geometry.Vector2 = {
			x: 0 - size.w / 2 + 0.0005,
			y: this._targetZone.cy - size.h / 2 - 0.0
		};

		this._homeField.reposition(
			ParticleLayoutType.IMAGE_PIXEL_DATA,
			position,
			size,
			{ pixelData: this._iconPixelData },
			this._iconParticles.slice(0, this._iconPixelData.length),
			100,
			false,
			this._expandObrbit.bind(this),
			this._mobileMode
		);

		this._iconScaleUpPassCount++;
		if (this._iconScaleUpPassCount >= this._iconScaleUpTotalPasses) {
			this._iconScaleUpActive = false;
		}
	}

	private _expandObrbit(p: HomeParticle) {
		p.icon.increaseOrbitExpand();
	}
}
